import { useEffect, useMemo, useState } from 'react'
import GShell from './GShell.jsx'
import { Title, Card, Modal, Field, Err, money, fmtDate } from './gui.jsx'
import { watchVendors, createVendor, updateVendor, deleteVendor } from '../../lib/vendors.js'
import { watchPurchaseOrders } from '../../lib/purchaseOrders.js'
import { watchProducts } from '../../lib/products.js'

const poTotal = (po) => Object.values(po.lines || {}).reduce((s, l) => s + (Number(l.qty) || 0) * (Number(l.cost) || 0), 0)

export default function GVendors() {
  const [raw, setRaw] = useState({})
  const [pos, setPos] = useState({})
  const [products, setProducts] = useState({})
  const [edit, setEdit] = useState(null)
  const [adding, setAdding] = useState(false)
  const [detail, setDetail] = useState(null)
  useEffect(() => { const a = watchVendors(setRaw), b = watchPurchaseOrders(setPos), c = watchProducts(setProducts); return () => { a(); b(); c() } }, [])

  const rows = useMemo(() => Object.values(raw || {}).filter(Boolean).sort((a, b) => (a.name || '').localeCompare(b.name || '')), [raw])
  const posFor = (vid) => Object.values(pos || {}).filter((p) => p && p.vendorId === vid)
  const prodCount = (vid) => Object.values(products || {}).filter((p) => p && p.vendorId === vid).length

  return (
    <GShell>
      <section>
        <Title title="Vendors" subtitle="Suppliers, reps, and order history." right={<button className="btn-primary text-xs" onClick={() => setAdding(true)}>+ Add Vendor</button>} />
      </section>
      <section className="pb-4">
        {rows.length === 0 ? <p className="text-sm text-gray-500">No vendors yet.</p> : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {rows.map((v) => {
              const vpos = posFor(v.id)
              return (
                <Card key={v.id}>
                  <div className="font-extrabold" style={{ color: 'var(--ge-primary)' }}>{v.name}</div>
                  <div className="text-xs text-gray-500 mt-0.5 space-y-0.5">
                    {v.salesRep ? <div>Rep: {v.salesRep}</div> : null}
                    {[v.phone, v.email].filter(Boolean).map((x, i) => <div key={i}>{x}</div>)}
                    {v.terms ? <div>Terms: {v.terms}</div> : null}
                    {v.brands ? <div>Brands: {v.brands}</div> : null}
                  </div>
                  <div className="text-xs text-gray-400 mt-2">{vpos.length} PO{vpos.length === 1 ? '' : 's'} · {prodCount(v.id)} products</div>
                  <div className="mt-2 flex gap-1 pt-2 border-t" style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
                    <button className="btn-secondary text-[11px]" onClick={() => setDetail({ v, vpos })}>Order history</button>
                    <button className="btn-ghost text-[11px]" onClick={() => setEdit(v)}>Edit</button>
                    <button className="btn-ghost text-[11px] ml-auto" style={{ color: '#B91C1C' }} onClick={async () => { if (window.confirm(`Delete ${v.name}?`)) await deleteVendor(v.id) }}>Delete</button>
                  </div>
                </Card>
              )
            })}
          </div>
        )}
      </section>

      {(adding || edit) ? <VendorModal v={edit} onClose={() => { setAdding(false); setEdit(null) }} /> : null}
      {detail ? (
        <Modal title={`${detail.v.name} — order history`} onClose={() => setDetail(null)}>
          {detail.vpos.length === 0 ? <p className="text-sm text-gray-500">No purchase orders yet.</p> : (
            <div className="space-y-1 text-sm">
              {detail.vpos.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0)).map((po) => (
                <div key={po.id} className="flex justify-between border-b py-1" style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
                  <span>{fmtDate(new Date(po.createdAt).toISOString().slice(0, 10))} · {po.status}</span>
                  <span className="font-bold">{money(poTotal(po))}</span>
                </div>
              ))}
            </div>
          )}
        </Modal>
      ) : null}
    </GShell>
  )
}

const VFIELDS = [['name', 'Name'], ['salesRep', 'Sales rep'], ['phone', 'Phone'], ['email', 'Email'], ['terms', 'Terms'], ['brands', 'Brands (comma-separated)']]

function VendorModal({ v, onClose }) {
  const [f, setF] = useState(v || Object.fromEntries(VFIELDS.map(([k]) => [k, ''])))
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }))
  const save = async () => { setBusy(true); setErr(''); try { v ? await updateVendor(v.id, f) : await createVendor(f); onClose() } catch (e) { setErr(e?.message || 'Failed'); setBusy(false) } }
  return (
    <Modal title={v ? `Edit — ${v.name}` : 'Add Vendor'} onClose={onClose}>
      <div className="grid sm:grid-cols-2 gap-2">
        {VFIELDS.map(([k, ph]) => <Field key={k} label={ph}><input className="input w-full" value={f[k] || ''} onChange={set(k)} /></Field>)}
      </div>
      <Field label="Notes"><input className="input w-full" value={f.notes || ''} onChange={set('notes')} /></Field>
      <div className="flex justify-end gap-2 mt-3"><button className="btn-ghost text-sm" onClick={onClose}>Cancel</button><button className="btn-primary text-sm" onClick={save} disabled={busy || !String(f.name || '').trim()}>{busy ? 'Saving…' : 'Save'}</button></div>
      <Err msg={err} />
    </Modal>
  )
}
